import React, { useState } from 'react';
import { capitalize } from 'lodash';
import moment from 'moment';

// Ant Design
import { Button, Divider, List, Modal, Progress, Result } from 'antd';

// css
import { css } from 'emotion';
import colors from '../../../style/colors';
import InlineSpinner from '../../ui/InlineSpinner';

import req from '../../../utilities/request-utility';

const SubmitAutomationComponent = props => {
	const { visible = false, onClose = () => {}, automation = null, onSuccess = () => {} } = props;

	const [successfullyProcessedData, setSuccessfullyProcessedData] = useState([]);
	const [submissionStatusData, setSubmissionStatusData] = useState({
		status: 'waiting',
		message: '',
		percentage: 0
	});

	const handleSubmit = async () => {
		try {
			if (!automation || !automation.automationToWorkTimeOBJ || !automation.dailySchedule) {
				throw new Error('Invalid automation data');
			}

			const { automationToWorkTimeOBJ, dailySchedule, blueCollarId } = automation;

			// blueCollarId is now provided by the backend in getAutomations response
			if (!blueCollarId) {
				throw new Error('BlueCollarId not found in automation data');
			}

			console.log(`Using blueCollarId: ${blueCollarId} for automation submission`);

			// Format the records similar to SelectAllComponent
			// In SelectAllComponent: selectedTimeRecords contains { id, data, type }
			// For automation: we have automationToWorkTimeOBJ with { automationID, workTimeID }
			// Filter out already submitted days
			const formattedRecords = automationToWorkTimeOBJ
				.map(mapping => {
					const day = dailySchedule.find(d => d.id === mapping.automationID);
					return {
						recordID: mapping.workTimeID,
						type: 'time',
						data: day,
						date: day?.date,
						isSubmitted: day?.isSubmitted || false
					};
				})
				.filter(record => !record.isSubmitted) // Only include unsubmitted records
				.sort((a, b) => {
					return new Date(a.date) - new Date(b.date);
				});

			let submittedRecords = 0;

			// Loop through each record (EXACT pattern from SelectAllComponent)
			for (const record of formattedRecords) {
				const { data, type, recordID } = record;

				const currentDate =
					type === 'time'
						? data.date
						: `${moment(data.startDate).format('YYYY-MM-DD')} - ${moment(data.endDate).format(
								'YYYY-MM-DD'
						  )}`;

				setSubmissionStatusData({
					status: 'submitting',
					message: `Submitting ${currentDate} (ID:${recordID})`,
					percentage: Math.round((submittedRecords / formattedRecords.length) * 100)
				});

				// EXACT same API call as SelectAllComponent
				await req().put(`semcotime/foremen-teams/users/${blueCollarId}/work-hours/${recordID}`, {
					...data,
					action: 'submit'
				});

				submittedRecords++;
				setSuccessfullyProcessedData(successfullyProcessedData => [
					...successfullyProcessedData,
					{
						date: currentDate,
						id: recordID,
						type
					}
				]);

				if (submittedRecords === formattedRecords.length) {
					setSubmissionStatusData({
						status: 'success',
						message: `Successfully submitted all Approved Records`,
						percentage: 100
					});

					// Call success callback
					onSuccess();
				}
			}
		} catch (error) {
			console.log('error: ', error);
			let errorMessage = 'Unspecified error';
			if (error && error.response && error.response.data) {
				if (error.response.data.message) errorMessage = error.response.data.message;
				else errorMessage = error.response.statusText;
			} else if (error.message) {
				errorMessage = error.message;
			}

			setSubmissionStatusData(submissionStatusData => ({
				...submissionStatusData,
				message: `${submissionStatusData.message.split(' ')[1]} ${
					submissionStatusData.message.split(' ')[2]
				} | Error: ${errorMessage}`,
				status: 'error'
			}));
		}
	};

	const handleRetry = () => {
		setSubmissionStatusData({
			status: 'waiting',
			message: '',
			percentage: 0
		});
		setSuccessfullyProcessedData([]);
	};

	const handleClose = () => {
		setSubmissionStatusData({
			status: 'waiting',
			message: '',
			percentage: 0
		});
		setSuccessfullyProcessedData([]);
		onClose();
	};

	const { message = '', status = 'waiting', percentage = 0 } = submissionStatusData;

	const progressBarStatus = { success: 'success', error: 'exception', submitting: 'active' }[status] || '';
	const resultIconStatus = { success: 'success', error: 'error' }[status] || '';

	const totalRecords = automation?.automationToWorkTimeOBJ?.length || 0;
	const submittedRecords = automation?.dailySchedule?.filter(day => day.isSubmitted).length || 0;
	const remainingRecords = totalRecords - submittedRecords;
	const dateRange = automation
		? `${moment(automation.tripStartDate).format('MM-DD-YYYY')} - ${moment(automation.tripEndDate).format(
				'MM-DD-YYYY'
		  )}`
		: '';

	const submitComponent = () => (
		<>
			{totalRecords === 0 && (
				<Result
					status="warning"
					title={'No Records in Automation'}
					subTitle={'This automation has no work hour records'}
				/>
			)}

			{status === 'waiting' && totalRecords > 0 && (
				<Result
					title={
						remainingRecords === 0
							? 'All Days Already Submitted'
							: `Submit ${remainingRecords} Remaining Day/s`
					}
					subTitle={
						<div>
							<div>{dateRange}</div>
							<div style={{ marginTop: '0.5rem', color: '#666' }}>
								<strong>{submittedRecords}</strong> of <strong>{totalRecords}</strong> days already
								submitted
							</div>
							{remainingRecords > 0 && (
								<div style={{ marginTop: '0.5rem' }}>Press "Submit Remaining" to continue</div>
							)}
						</div>
					}
					extra={
						remainingRecords > 0 && (
							<Button onClick={handleSubmit} type="primary" key="console">
								Submit Remaining ({remainingRecords})
							</Button>
						)
					}
				/>
			)}

			{status !== 'waiting' && totalRecords > 0 && (
				<>
					<Result
						icon={status === 'submitting' ? <InlineSpinner /> : ''}
						status={resultIconStatus}
						subTitle={message}
						extra={
							<>
								<Progress percent={percentage} status={progressBarStatus} />
								{status === 'success' && (
									<Button type="primary" onClick={handleClose} key="console">
										OK
									</Button>
								)}
								{status === 'error' && (
									<Button type="primary" onClick={handleRetry} key="console">
										Retry
									</Button>
								)}
							</>
						}
					/>

					<Divider orientation="left">Successfully Submitted</Divider>
					<List
						size="small"
						bordered
						dataSource={successfullyProcessedData}
						renderItem={item => (
							<List.Item extra={<h4>{`${capitalize(item.type)}`}</h4>}>
								<List.Item.Meta title={item.date} description={`[${item.id}]`} />
							</List.Item>
						)}
					/>
				</>
			)}
		</>
	);

	return (
		<Modal
			visible={visible}
			onCancel={handleClose}
			footer={null}
			width={600}
			title="Submit Automation"
			closable={status !== 'submitting'}>
			<div className={componentStyles()}>{submitComponent()}</div>
		</Modal>
	);
};

const componentStyles = () => css`
	background-color: ${colors.white};
	padding: 1rem;
`;

export default SubmitAutomationComponent;
